/**
 * NT1's and NT2's calls. Every one goes through `../../api/client.js` and nothing else.
 *
 * WP-M's five routes already served the DATA; what they did not serve was the CHROME, so
 * both screens were rendering their titles, tabs, intro, footer and quiet-hours row from
 * a `data.ts` at runtime. `apps/api/src/notifications/screens.ts` is where that moved,
 * and this is the client half.
 *
 * ── PATCH, NOT PUT, AND THAT IS A REPORTED GAP ──────────────────────────────
 * The write routes are `PUT /prefs` and `PUT /prefs/quiet-hours`, which is the right verb
 * for an idempotent replace. `api/client.ts` exposes four verbs and `put` is not among
 * them, and that file is not this work's to edit — so the API accepts PATCH on both as
 * well, and this module uses it. The root fix is a `put` on the client or a house rule
 * that partial writes are PATCH; both are named in the report.
 *
 * The row shapes are the API's, not the screens'. `dot`, `title_weight` and
 * `highlighted` are DERIVED SERVER-SIDE from the read state and the category — the amber
 * dot is the unread `action_required` row and nothing else (docs/02 § colour: amber is
 * one element per screen) — so two clients cannot disagree about which row is shouting.
 */
import { api } from '../../api/client.js';
import type { NotifCategory, NotifMode } from './types.js';

/** The three states the row dot takes. Not a scale; see the API module for why. */
export type NotifDot = 'attention' | 'unread' | 'read';

export interface NotificationView {
  id: string;
  category: NotifCategory;
  label_bn: string;
  /** The drawn timestamp. `null` on a row written before the column existed. */
  time_bn: string | null;
  title_bn: string;
  body_bn: string;
  dot: NotifDot;
  title_weight: 500 | 600;
  highlighted: boolean;
  action_route: string | null;
  /** docs/13 § UI: an `action_required` item shows what is blocked and on whom. */
  blocked_what_bn: string | null;
  blocked_on_bn: string | null;
}

export interface NotificationCentre {
  title_bn: string;
  mark_all_read_bn: string;
  tabs: Array<{ key: 'all' | NotifCategory; label_bn: string }>;
  prefs_link_bn: string;
  notifications: NotificationView[];
  unread_count: number;
}

export interface PrefScreenRow {
  category: NotifCategory;
  label_bn: string;
  hint_bn: string | null;
  mode: NotifMode;
  mode_label_bn: string;
  locked: boolean;
}

export interface PrefsResponse {
  /** All nine categories, resolved. NT2 renders `screen.rows`; this is the whole truth. */
  prefs: Array<{ category: NotifCategory; mode: NotifMode }>;
  screen: {
    title_bn: string;
    intro_bn: string;
    footer_bn: string;
    modes: Array<{ id: NotifMode; label_bn: string }>;
    /** The SIX rows NT2 draws, in the frame's order. */
    rows: PrefScreenRow[];
    quiet_hours: { label_bn: string; hint_bn: string; enabled: boolean };
  };
}

export function fetchNotifications(signal?: AbortSignal): Promise<NotificationCentre> {
  return api.get<NotificationCentre>('/v1/notifications', { signal });
}

export function markAllRead(): Promise<{ ok: true }> {
  return api.post<{ ok: true }>('/v1/notifications/read-all');
}

export function markRead(id: string): Promise<{ ok: true }> {
  return api.post<{ ok: true }>(`/v1/notifications/${encodeURIComponent(id)}/read`);
}

export function fetchPrefs(signal?: AbortSignal): Promise<PrefsResponse> {
  return api.get<PrefsResponse>('/v1/notifications/prefs', { signal });
}

/**
 * One category's mode.
 *
 * `critical` is refused by the route, not merely hidden by the screen: NT2 draws it as a
 * disabled chip reading সবসময়, and a second client that offered the choice would write a
 * row `resolveMode` ignores and leave the two disagreeing about what the user chose.
 */
export function setPref(category: NotifCategory, mode: NotifMode): Promise<PrefsResponse> {
  return api.patch<PrefsResponse>('/v1/notifications/prefs', { category, mode });
}

/** NT2's one drawn switch. A route of its own, because it is a window and not a category. */
export function setQuietHours(enabled: boolean): Promise<PrefsResponse> {
  return api.patch<PrefsResponse>('/v1/notifications/prefs/quiet-hours', { enabled });
}
